import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setFlashToast } from '../../../utils/flashToast';
import * as rtiService from '../../../services/rightToInformationCaseRequests';
import { getAppUserId } from '../../../services/activeAppUser';
import { getInternalUserProfileById, type InternalUserProfile } from '../../../services/userProfile';
import {
  focusFirstInvalid,
  validateRequired,
  type RequiredErrors,
  type RequiredFieldSpec,
} from '../../../utils/requiredValidation';
import { useRightToInformationWizardContext } from '../../../hooks/useCaseRequestWizard';
import { useRightToInformationLookups } from './useRightToInformationLookups';
import { formatDisplayDate } from '../../../utils/dateFormat';
import { CriminalReviewDocumentsTable } from '../criminal/CriminalReviewDocumentsTable';
import { apiOrLookupLabel } from '../../view-request/viewRequestHelpers';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../criminal/CriminalReviewUi';
import RightToInformationCoverNoteModal from './RightToInformationCoverNoteModal';
import { rightToInformationDraftToCoverNoteFallback } from './rightToInformationCoverNote';
import { RightToInformationWizardActions } from './RightToInformationWizardActions';
import { rightToInformationWizardPath } from './RightToInformationWizardShell';
import { preferredAccessLabels } from './rightToInformationHelpers';
import { useRole } from '../../../rbac/hooks';
import { tasksListPathForRole } from '../../../rbac/tasksListPath';

function yesNoLabel(v: string): string {
  if (v === 'yes') return 'Yes';
  if (v === 'no') return 'No';
  return '—';
}

export default function RightToInformationReviewPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { roleCode } = useRole();
  const tasksListPath = tasksListPathForRole(roleCode);
  const { draft, setDraft } = useRightToInformationWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const [profile, setProfile] = useState<InternalUserProfile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCoverNote, setShowCoverNote] = useState(false);
  const [coverNoteRequestId, setCoverNoteRequestId] = useState('');
  const [coverNoteFileRef, setCoverNoteFileRef] = useState('');
  const lookups = useRightToInformationLookups('review');

  useEffect(() => {
    let mounted = true;
    const appUserId = getAppUserId();
    if (!appUserId) return () => {
      mounted = false;
    };
    void getInternalUserProfileById(appUserId).then((r) => {
      if (mounted && r.success) setProfile(r.data);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const preferredAccessDisplay =
    draft.preferredMethodOfAccessNames.filter(Boolean).join(', ') ||
    preferredAccessLabels(draft.preferredMethodOfAccessIds, lookups.preferredMethodOfAccess).join(', ') ||
    '—';

  const coverNoteFallback = useMemo(
    () =>
      rightToInformationDraftToCoverNoteFallback(draft, {
        fileRef: coverNoteFileRef || draft.referenceNo,
        submittedDivisionName: profile?.divisionName || draft.submittedDivisionName,
      }),
    [coverNoteFileRef, draft, profile?.divisionName],
  );

  const submit = async () => {
    if (submitting) return;

    const specs: RequiredFieldSpec[] = [
      { key: 'confirmCorrect', label: 'Confirmation', value: draft.confirmCorrect ? 'yes' : '', elementId: 'rti-confirm' },
    ];
    const reqErrs = validateRequired(specs);
    setErrors(reqErrs);
    if (Object.keys(reqErrs).length) {
      focusFirstInvalid(specs, reqErrs);
      return;
    }

    setSubmitting(true);
    const r = await rtiService.saveRightToInformationRequest(draft, {
      isDraft: false,
      allSteps: true,
      confirmedCorrect: true,
    });
    if (!r.success) {
      setSubmitting(false);
      setFlashToast({ message: r.message ?? 'Submission failed.', variant: 'error' });
      return;
    }
    setDraft((d) => ({
      ...d,
      draftRequestId: r.data.requestId,
      referenceNo: r.data.fileReferenceNumber ?? d.referenceNo,
    }));
    setFlashToast({ message: 'Case Request submitted successfully.', variant: 'success' });
    setCoverNoteRequestId(r.data.requestId);
    setCoverNoteFileRef(r.data.fileReferenceNumber ?? draft.referenceNo ?? '');
    setShowCoverNote(true);
    setSubmitting(false);
  };

  const closeCoverNote = () => {
    setShowCoverNote(false);
    setCoverNoteRequestId('');
    setCoverNoteFileRef('');
    nav(tasksListPath);
  };

  const reviewCrumbTo = rightToInformationWizardPath(1, draft.draftRequestId);
  const editTo = (step: 1 | 2 | 3 | 4) => rightToInformationWizardPath(step, draft.draftRequestId);

  return (
    <div className="ccr-review-page">
      <RightToInformationCoverNoteModal
        isOpen={showCoverNote}
        caseRequestId={coverNoteRequestId}
        fileRefFallback={coverNoteFileRef}
        coverNoteFallback={coverNoteFallback}
        showSuccessBanner
        onClose={closeCoverNote}
      />
      <div className="bc">
        <Link to={tasksListPath}>{t('nav.tasksAndCases')}</Link>
        <span className="bc-sep">/</span>
        <Link to={reviewCrumbTo}>Create Case Request</Link>
        <span className="bc-sep">/</span>
        Review Before Submission
      </div>
      <div className="pg-title">Review Case Request Details</div>

      <ReviewSection step="Step 01" title="Case Overview" editTo={editTo(1)}>
        <ReviewSubBlock title="Case Categorization">
          <ReviewGrid cols={4}>
            <ReviewField label="File Category" value={draft.fileCategory} />
            <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
          </ReviewGrid>
          <ReviewChips items={draft.applicableAreas} />
        </ReviewSubBlock>
        <ReviewSubBlock title="Requester Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Name of Requestor" value={draft.requestorName} />
            <ReviewField label="NIC/ Passport Number" value={draft.nicOrPassportNumber} />
            <ReviewField label="Contact Number" value={draft.contactNumber} />
            <ReviewField label="Email Address" value={draft.emailAddress} />
            <ReviewField
              label="Is the requestor a citizen of Sri Lanka?"
              value={yesNoLabel(draft.isSriLankanCitizen)}
            />
          </ReviewGrid>
          <ReviewField label="Address" value={draft.address} fullWidth />
        </ReviewSubBlock>
        <ReviewSubBlock title="Information Request Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Specific Public Authority Name" value={draft.specificPublicAuthorityName} />
            <ReviewField label="Information Requested Time Period" value={draft.informationRequestedTimePeriod} />
          </ReviewGrid>
          <ReviewField label="Requested Information Details" value={draft.requestedInformationDetails} fullWidth />
        </ReviewSubBlock>
        <ReviewSubBlock title="Additional Information">
          <ReviewField label="Preferred Method of Access" value={preferredAccessDisplay} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField
              label="Preferred Language for Response"
              value={apiOrLookupLabel(
                draft.preferredLanguageName,
                draft.preferredLanguageId,
                lookups.preferredLanguages,
              )}
            />
            <ReviewField
              label="Does this request involve life or personal liberty of a citizen?"
              value={yesNoLabel(draft.involvesLifeOrPersonalLiberty)}
            />
          </ReviewGrid>
          <ReviewField label="Justification" value={draft.justification} fullWidth />
          <ReviewField label="Additional Notes" value={draft.additionalNotes} fullWidth />
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 02" title="Timeline" editTo={editTo(2)}>
        <ReviewSubBlock title="Timeline">
          <ReviewGrid cols={3}>
            <ReviewField
              label="Urgency Level"
              value={apiOrLookupLabel(draft.urgencyLevelName, draft.urgencyLevelId, lookups.urgencyLevels)}
            />
            <ReviewField label="Deadline (if any)" value={formatDisplayDate(draft.deadline)} />
            <ReviewField label="Urgency Justification" value={draft.urgencyJustification} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 03" title="Submission" editTo={editTo(3)}>
        <ReviewSubBlock title="Request Submitter's Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Submitted Division/ Unit" value={profile?.divisionName?.trim() || '—'} />
            <ReviewField label="Designation" value={profile?.designation?.trim() || '—'} />
            <ReviewField
              label="Submitted by"
              value={profile?.userDisplayName?.trim() || profile?.username?.trim() || '—'}
            />
            <ReviewField label="Email Address" value={profile?.username?.trim() || '—'} />
            <ReviewField label="Contact Number" value={profile?.contactNumber?.trim() || '—'} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 04" title="Upload Documents" editTo={editTo(4)}>
        <ReviewSubBlock>
          <CriminalReviewDocumentsTable documents={draft.documents} />
        </ReviewSubBlock>
      </ReviewSection>

      <div className="confirm-section ccr-review-confirm">
        <label className="ccr-chk" id="rti-confirm">
          <input
            type="checkbox"
            checked={draft.confirmCorrect}
            disabled={submitting}
            onChange={(e) => {
              setDraft((d) => ({ ...d, confirmCorrect: e.target.checked }));
              if (e.target.checked) setErrors((er) => ({ ...er, confirmCorrect: '' }));
            }}
          />
          <span className="ccr-review-confirm-text">
            <span className="ccr-review-confirm-main">
              I confirm the above information is correct <span className="req">*</span>
            </span>
            <span className="ccr-review-confirm-hint">Please confirm before submitting this request to AGD.</span>
          </span>
        </label>
        {errors.confirmCorrect ? <div className="ccr-review-confirm-err">{errors.confirmCorrect}</div> : null}
      </div>

      <RightToInformationWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={4}
        onBack={() => nav(editTo(4))}
        showSubmit
        submitting={submitting}
        onSubmit={() => void submit()}
        reviewMode
      />
    </div>
  );
}
